import React, { useState, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Link as MuiLink,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import api from '../../config/api';
import OtherToolsSection from '../OtherToolsSection';

const faqs = [
  {
    q: 'What is an SPF record?',
    a: 'An SPF record is a DNS record that specifies which mail servers are authorized to send email on behalf of your domain.'
  },
  {
    q: 'Why does my SPF record fail?',
    a: 'Common reasons include syntax errors, missing or incorrect IP addresses, or exceeding the DNS lookup limit.'
  },
  {
    q: 'How do I fix SPF issues?',
    a: 'Update your DNS with the correct SPF record, ensure all authorized mail servers are included, and stay within the lookup limit.'
  },
  {
    q: 'What is the difference between include, ip4, and mx mechanisms?',
    a: 'Include adds another domain\'s SPF record, ip4 specifies IPv4 addresses, and mx uses the domain\'s MX records.'
  },
  {
    q: 'How many DNS lookups are allowed in SPF?',
    a: 'SPF allows a maximum of 10 DNS lookups. Each include, a, mx, ptr, and exists mechanism counts as one lookup.'
  },
  {
    q: 'Can I have multiple SPF records?',
    a: 'No, a domain must have only one SPF record. Multiple records will cause SPF to fail.'
  },
  {
    q: 'What is the all mechanism in SPF?',
    a: 'The all mechanism specifies what to do with emails that don\'t match any other mechanisms. Common values are -all (hard fail) or ~all (soft fail).'
  },
  {
    q: 'How do I test my SPF record?',
    a: 'Use online tools like this SPF Analyzer or command-line tools (dig, nslookup) to check and validate your SPF record.'
  },
  {
    q: 'What happens if my SPF record is too long?',
    a: 'SPF records longer than 255 characters must be split into multiple strings in the DNS. If not, the record may be ignored.'
  },
  {
    q: 'What is an SPF failure?',
    a: 'An SPF failure occurs when an email is sent from an unauthorized mail server, and the policy is set to -all (hard fail).'
  }
];

interface SPFMechanism {
  type: string;
  value: string;
}

interface SPFNode {
  domain: string;
  record: string;
  mechanisms: SPFMechanism[];
  pass: boolean;
}

interface SPFResult {
  domain: string;
  timestamp: string;
  spf: string;
  spf_tree: SPFNode;
}

const SPFAnalyzer: React.FC = () => {
  const [domain, setDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SPFResult | null>(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const resultRef = useRef<HTMLDivElement>(null);

  const validateDomain = (d: string) => /^([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/.test(d);

  const handleAnalyze = async () => {
    setError('');
    setResult(null);
    if (!domain) {
      setError('Please enter a domain.');
      return;
    }
    if (!validateDomain(domain)) {
      setError('Please enter a valid domain.');
      return;
    }
    setLoading(true);
    try {
      const response = await api.get(`/api/dns/check/SPF?domain=${encodeURIComponent(domain)}`);
      if (!response.data || !response.data.spf) {
        setError('No SPF record found for this domain.');
        setResult(null);
      } else {
        // Parse the SPF record and build the tree
        const spfRecord = response.data.spf;
        const tree: SPFNode = {
          domain: domain,
          record: spfRecord,
          mechanisms: parseSPFRecord(spfRecord),
          pass: true
        };
        setResult({
          ...response.data,
          spf_tree: tree
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch SPF data.');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const parseSPFRecord = (record: string): SPFMechanism[] => {
    if (!record) return [];
    
    const mechanisms: SPFMechanism[] = [];
    const parts = record.split(' ');
    
    for (const part of parts) {
      if (part.startsWith('v=spf1')) continue;
      
      let type = 'unknown';
      let value = part;
      
      if (part.startsWith('include:')) {
        type = 'include';
        value = part.substring(8);
      } else if (part.startsWith('ip4:')) {
        type = 'ip4';
        value = part.substring(4);
      } else if (part.startsWith('ip6:')) {
        type = 'ip6';
        value = part.substring(4);
      } else if (part.startsWith('a:')) {
        type = 'a';
        value = part.substring(2);
      } else if (part.startsWith('mx:')) {
        type = 'mx';
        value = part.substring(3);
      } else if (part === 'all') {
        type = 'all';
        value = 'all';
      }
      
      mechanisms.push({ type, value });
    }
    
    return mechanisms;
  };

  const handleDownloadPDF = async () => {
    if (!result || !result.spf) return;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let y = pageHeight / 2 - 60;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(32);
    doc.setTextColor('#0B60B1');
    doc.text('SPF AUDIT REPORT', 60, y);
    y += 40;
    doc.setFontSize(18);
    doc.setTextColor('#222');
    doc.text(`Domain: ${domain}`, 60, y);
    y += 28;
    doc.setFontSize(12);
    doc.setTextColor('#888');
    const now = new Date();
    doc.text(`Date: ${now.toLocaleDateString()}   Year: ${now.getFullYear()}`, 60, y);
    y += 22;
    doc.setFontSize(10);
    doc.setTextColor('#888');
    doc.text('Generated by: QuillBee – Deliverability Agent for ImpactQuill', 60, y);
    doc.addPage();
    // Header for page 2
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor('#0B60B1');
    doc.text(`SPF report for ${domain}`, 40, 110);
    let y2 = 140;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor('#0B60B1');
    doc.text('SPF Record:', 40, y2);
    y2 += 18;
    doc.setFont('courier', 'normal');
    doc.setFontSize(11);
    doc.setTextColor('#222');
    const spfLines = doc.splitTextToSize(result.spf, pageWidth - 80);
    spfLines.forEach((line: string) => {
      doc.text(line, 40, y2);
      y2 += 16;
    });
    y2 += 10;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(result.spf ? '#388e3c' : '#d32f2f');
    doc.text(`Status: ${result.spf ? 'PASS' : 'FAIL'}`, 40, y2);
    y2 += 24;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor('#0B60B1');
    doc.text('SPF Mechanisms:', 40, y2);
    y2 += 18;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.setTextColor('#222');
    result.spf_tree.mechanisms.forEach((mech: SPFMechanism) => {
      doc.text(`• ${mech.type}: ${mech.value}`, 60, y2);
      y2 += 18;
    });
    doc.save(`spf-analysis-${domain}.pdf`);
  };

  const handleDownloadJPG = async () => {
    if (!resultRef.current) return;
    const canvas = await html2canvas(resultRef.current);
    const link = document.createElement('a');
    link.download = `spf-analysis-${domain}.jpg`;
    link.href = canvas.toDataURL('image/jpeg', 1.0);
    link.click();
  };

  return (
    <Box sx={{ bgcolor: '#f7fafd', minHeight: '100vh', py: 0 }}>
      {/* Hero Banner */}
      <Box sx={{ bgcolor: '#0B60B1', color: '#fff', py: 6, textAlign: 'center' }}>
        <Typography variant="h3" fontWeight={700} gutterBottom>SPF Analyzer</Typography>
        <Typography variant="h6" maxWidth={600} mx="auto">
          Analyze and visualize your SPF record for best practices and deliverability.
        </Typography>
      </Box>

      {/* Input Section */}
      <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="subtitle1" mb={2}>Enter your domain to check SPF:</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, width: '100%', maxWidth: 400 }}>
          <TextField
            label="Domain"
            variant="outlined"
            value={domain}
            onChange={e => setDomain(e.target.value.trim())}
            fullWidth
            disabled={loading}
            error={!!error}
            helperText={error}
            onKeyDown={e => {
              if (e.key === 'Enter' && !loading && domain) handleAnalyze();
            }}
          />
          <Button variant="contained" color="primary" onClick={handleAnalyze} disabled={loading || !domain} sx={{ minWidth: 120 }}>
            {loading ? <CircularProgress size={24} /> : 'Analyze'}
          </Button>
        </Box>
        {loading && <CircularProgress sx={{ mt: 3 }} />}
        {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
      </Box>

      {/* Result Section */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {result && result.spf && (
        <Card ref={resultRef} sx={{ width: '100%', maxWidth: 900, mx: 'auto', mb: 4, p: 2, boxShadow: 6, borderRadius: 3, bgcolor: '#fff' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <MuiLink href={`https://${domain}`} target="_blank" rel="noopener noreferrer" underline="hover" variant="subtitle1" sx={{ fontWeight: 600, fontSize: 18 }}>
                  {domain}
                </MuiLink>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title={copied ? 'Copied!' : 'Copy SPF Record'}>
                  <IconButton onClick={() => { navigator.clipboard.writeText(result.spf); setCopied(true); setTimeout(() => setCopied(false), 1200); }}>
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download PDF">
                  <IconButton onClick={handleDownloadPDF}>
                    <PictureAsPdfIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download as JPG">
                  <IconButton onClick={handleDownloadJPG}>
                    <ImageIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ mb: 2 }}>
              <Chip
                label={result.spf ? 'PASS' : 'FAIL'}
                color={result.spf ? 'success' : 'error'}
                sx={{ fontWeight: 700, fontSize: 16, px: 2, py: 1, mb: 1 }}
              />
            </Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>SPF Record</Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f7fafd', mb: 2 }}>
              <Typography variant="body1" component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: 'monospace', fontSize: 16 }}>
                {result.spf}
              </Typography>
            </Paper>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>SPF Lookup Tree</Typography>
            <Box sx={{ mt: 2 }}>
              {result.spf_tree.mechanisms.map((mech, idx) => (
                <Box key={idx} sx={{ mb: 1 }}>
                  {mech.type === 'include' ? (
                    <Box sx={{ ml: 2, pl: 2, borderLeft: '2px solid #e0e0e0' }}>
                      <Typography variant="body2" color="text.secondary">include:</Typography>
                      <Typography variant="body1">{mech.value}</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ ml: 2, pl: 2, borderLeft: '2px solid #e0e0e0' }}>
                      <Typography variant="body2" color="text.secondary">{mech.type}:</Typography>
                      <Typography variant="body1">{mech.value}</Typography>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* FAQ Section */}
      <Container maxWidth="md" sx={{ mt: 6, mb: 8 }}>
        <Paper sx={{ p: 4, borderRadius: 3, boxShadow: 2 }}>
          <Typography variant="h5" fontWeight={700} textAlign="center" mb={3}>SPF FAQ</Typography>
          {faqs.map((faq, i) => (
            <Accordion key={i} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight={600}>{faq.q}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary">{faq.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>
      </Container>

      <OtherToolsSection current="SPF Analyzer" />

      {/* CTA Section: Need help with email deliverability? */}
      <Box sx={{ py: 6, textAlign: 'center', bgcolor: '#e3f0fc', mt: 8 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          {/* Animated Quillbee Mascot in white circle */}
          <Box
            sx={{
              width: 90,
              height: 90,
              borderRadius: '50%',
              background: '#fff',
              boxShadow: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
              animation: 'floatBee 3s ease-in-out infinite',
              '@keyframes floatBee': {
                '0%': { transform: 'translateY(0px)' },
                '50%': { transform: 'translateY(-12px)' },
                '100%': { transform: 'translateY(0px)' },
              },
            }}
          >
            <Box
              component="img"
              src="/images/quillbee.png"
              alt="Quillbee Mascot"
              sx={{ width: 60, height: 60, objectFit: 'contain' }}
            />
          </Box>
          <Typography variant="h5" fontWeight={700} mb={1} color="primary">
            Need help with email deliverability?
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={3} maxWidth={420}>
            Our experts can help you improve your inbox placement and resolve deliverability issues. Get personalized solutions for your email infrastructure.
          </Typography>
          <Button variant="contained" color="primary" size="large" href="/contact" sx={{ boxShadow: 2, px: 4, py: 1.5, fontWeight: 700 }}>
            Contact Our Experts
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default SPFAnalyzer; 